'use client'

import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { usePostLogin } from '@/hooks/react-query/auth/use-post-login'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { useRouter } from '@/i18n/navigation'
import { zodResolver } from '@hookform/resolvers/zod'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

import { ImageCM } from '@/components/extension/image-cm'
import { Divider } from '@/components/ui/divider'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { cn } from '@/lib/utils'
import { SessionStorage } from '@/lib/utils/session-storage'
import { validateEmail } from '@/lib/utils/validation'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import { __cAuthService } from '@/services/client/__c.auth.service'
import { RANKING_LABEL_VN } from '@/types/auth'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { ForgotPasswordForm } from './forgot-password-form'
import { OtpForm } from './otp-form'
import QRLogin from './qr-login'
import { openRegister } from './open-auth'
import { VerifyPhone } from './verify-phone'

type LoginFormProps = {
  isPage?: boolean
  callback?: () => void
}

export function LoginForm({ isPage, callback }: LoginFormProps) {
  // get redirect url from url params
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const redirectUrlFromParams = searchParams.get('redirect')
  const redirectUrlFromStorage = SessionStorage.get<string>('auth_redirect_url')
  const [isQrMode, setIsQrMode] = useState(false)

  // Lấy redirectUrl từ query params hoặc sessionStorage (fallback)
  // Nếu không có redirectUrl từ params và không phải isPage, lấy URL hiện tại
  // Sử dụng window.location để đảm bảo lấy đúng URL hiện tại (usePathname có thể không cập nhật kịp khi popup mở)
  const redirectUrl =
    redirectUrlFromParams ||
    redirectUrlFromStorage ||
    (!isPage
      ? (() => {
          // Lấy pathname từ window.location để đảm bảo chính xác
          const currentPathname =
            typeof window !== 'undefined' ? window.location.pathname : pathname
          const currentSearch = searchParams.toString()
          return currentPathname + (currentSearch ? `?${currentSearch}` : '')
        })()
      : null)

  // Lưu redirectUrl vào sessionStorage nếu có (để đảm bảo không bị mất khi verify phone)
  // Khi isPage=true (trang login page, không phải popup), lưu redirectUrl từ query params hoặc storage
  useEffect(() => {
    if (isPage) {
      // Ưu tiên lưu từ query params nếu có (mới nhất)
      if (redirectUrlFromParams) {
        SessionStorage.setWithExpiry(
          'auth_redirect_url',
          redirectUrlFromParams,
          10
        )
      }
      // Nếu không có từ params nhưng có từ storage, giữ nguyên (đã được lưu trước đó)
      // Không cần lưu lại vì đã có sẵn trong storage
    }
  }, [isPage, redirectUrlFromParams])

  const { refetch: refetchUser } = useUserQuery()
  const router = useRouter()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const trackEvent = useTrackEvent()
  const postLogin = usePostLogin()
  const t = useTranslations('login')

  // Định nghĩa formSchema bên trong component để dùng được t
  const formSchema = z.object({
    username: z
      .string()
      .min(1, t('required_username'))
      .refine(
        (value) => {
          const isEmail = validateEmail(value)
          const phoneValidate = value?.length === 10
          return isEmail || phoneValidate
        },
        (value) => {
          if (value.includes('@')) {
            return { message: t('invalid_email') }
          }
          if (typeof +value === 'number' && !isNaN(+value)) {
            return { message: t('invalid_phone') }
          }
          return { message: t('invalid_username') }
        }
      ),
    password: z
      .string()
      .min(1, t('required_password'))
      .min(6, t('password_too_short')),
  })

  const handleToggleQrMode = useCallback(() => {
    setIsQrMode((prev) => !prev)
  }, [])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const processAfterLoginSuccess = async () => {
    // Tránh refresh toàn trang; chỉ invalidate có chọn lọc và điều hướng nếu có
    const { data: user } = await refetchUser()

    if (user) {
      trackEvent({
        event: ETrackingEvent.UserLogin,
        user_id: user.user_id,
        user_type: RANKING_LABEL_VN[user?.ranking?.ranking_id],
      })
    }

    // Lấy redirectUrl từ sessionStorage nếu không có từ component state
    // (quan trọng khi verify phone/OTP vì redirectUrl có thể bị mất trong closure)
    const finalRedirectUrl =
      redirectUrl || SessionStorage.get<string>('auth_redirect_url') || null

    // Thống nhất logic: sử dụng postLogin cho cả isPage=true và isPage=false để đảm bảo nhất quán
    postLogin({ user, redirectUrl: finalRedirectUrl })

    if (isPage) {
      // Nếu không có redirectUrl, redirect về trang chủ
      if (!finalRedirectUrl) {
        router.push('/')
      }
      // Nếu có redirectUrl, postLogin sẽ xử lý redirect
    } else {
      callback?.()
      hidePopup()
    }

    toast.message({
      title: t('success_title'),
      message: t('success_message'),
    })
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setError('')
      const res = await __cAuthService.login(values)

      // Nếu chưa verify phone thì hiển thị popup verify phone
      // Lưu redirectUrl vào sessionStorage để đảm bảo không bị mất khi verify phone
      if (!res?.data?.isset_phone) {
        if (redirectUrl) {
          SessionStorage.setWithExpiry('auth_redirect_url', redirectUrl, 10)
        }
        showPopup(
          <VerifyPhone
            user={{
              fullname: res?.data?.fullname ?? '',
              email: res?.data?.email ?? '',
              id: res?.data?.user_id ?? '',
            }}
            isConfirm={res?.data?.is_login}
            onSuccess={processAfterLoginSuccess}
          />
        )
        return
      }

      if (res?.data?.isset_phone && !res?.data?.is_login) {
        // Lưu redirectUrl vào sessionStorage để đảm bảo không bị mất khi verify OTP
        if (redirectUrl) {
          SessionStorage.setWithExpiry('auth_redirect_url', redirectUrl, 10)
        }
        showPopup(
          <OtpForm
            phone={res?.data?.phone ?? ''}
            onSuccess={processAfterLoginSuccess}
            type='login'
            isAuth={true}
            bodyAuth={{
              user_id: res?.data?.user_id ?? '',
              fullname: res?.data?.fullname ?? '',
              email: res?.data?.email ?? '',
              phone: res?.data?.phone ?? '',
            }}
            onClose={() => hidePopup()}
          />
        )
        return
      }

      // Nếu đã verify phone thì hiển thị toast login success
      if (res.state && res?.status === 200) {
        await processAfterLoginSuccess()
      } else {
        setError(t('error'))
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message)
      } else {
        setError(t('error'))
      }
    }
  }

  const handleThirdPartyLogin =
    (provider: 'google' | 'facebook') => async () => {
      // TODO: Tạm thời cho dùng v1
      // if (provider === 'google') {
      //   window.location.href = 'https://coolmate.me/google/login'
      //   return
      // }

      // if (provider === 'facebook') {
      //   window.location.href = 'https://coolmate.me/facebook/login'
      // }

      try {
        // Lấy redirectUrl hiện tại từ window.location để đảm bảo chính xác
        // (redirectUrl từ component state có thể không cập nhật kịp)
        const currentRedirectUrl =
          redirectUrl ||
          (!isPage && typeof window !== 'undefined'
            ? window.location.pathname +
              (window.location.search ? window.location.search : '')
            : null)

        // Lưu redirectUrl vào sessionStorage để callback có thể lấy lại
        // (vì Google OAuth có thể không giữ query params khi redirect về callback)
        if (currentRedirectUrl) {
          SessionStorage.setWithExpiry(
            'auth_redirect_url',
            currentRedirectUrl,
            10
          ) // 10 phút
        }

        const thirdPartyRedirectUrl =
          await __cAuthService.loginThirdParty(provider)

        // Thêm redirect param vào URL để callback có thể redirect về đúng trang
        // Google/Facebook sẽ giữ nguyên query params khi redirect về callback URL
        let finalUrl = thirdPartyRedirectUrl
        if (currentRedirectUrl) {
          try {
            // Xử lý cả absolute và relative URL
            const url = thirdPartyRedirectUrl.startsWith('http')
              ? new URL(thirdPartyRedirectUrl)
              : new URL(thirdPartyRedirectUrl, window.location.origin)
            url.searchParams.set('redirect', currentRedirectUrl)
            finalUrl = url.toString()
          } catch (error) {
            // Fallback: append query param thủ công nếu URL parsing fail
            const separator = thirdPartyRedirectUrl.includes('?') ? '&' : '?'
            finalUrl = `${thirdPartyRedirectUrl}${separator}redirect=${encodeURIComponent(currentRedirectUrl)}`
          }
        }

        window.location.href = finalUrl
      } catch (error) {
        console.error(error)
        toast.message({
          title: t('fail_title'),
          message: t('fail_message', { provider }),
        })
      }
    }

  if (isQrMode) {
    return <QRLogin onBack={handleToggleQrMode} />
  }

  return (
    <>
      <div className='text-neutral-800'>
        <ImageCM
          src={'/March2024/mceclip4_81.jpg'}
          width={132}
          height={22}
          alt={t('logo_alt')}
        />

        <h3 className='mt-4 font-criteria text-[28px] font-medium leading-8 tracking-normal text-neutral-900'>
          {t('privileges')}
        </h3>

        <p className='mt-3.5 font-sans text-xs'>{t('benefit')}</p>
        <div className='mb-4 mt-1 grid grid-cols-3 gap-2'>
          <div className='flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-2 shadow-sm'>
            <ImageCM
              src={'/image/September2025/mceclip4_37.png'}
              width={32}
              height={32}
              alt={t('voucher_alt')}
            />
            <div>
              <p className='text-sm font-medium !leading-tight text-neutral-900 md:text-base'>
                {t.rich('voucher_title', {
                  newline: () => <br />,
                })}
              </p>
            </div>
          </div>
          <div className='flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-2 shadow-sm'>
            <ImageCM
              src={'/image/September2025/mceclip5_49.png'}
              width={32}
              height={32}
              alt={t('gift_alt')}
            />
            <div>
              <p className='text-sm font-medium !leading-tight text-neutral-900 md:text-base'>
                {t.rich('gift_title', {
                  newline: () => <br />,
                })}
              </p>
            </div>
          </div>
          <div className='flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-2 shadow-sm'>
            <ImageCM
              src={'/image/September2025/mceclip6_7.png'}
              width={32}
              height={32}
              alt={t('cashback_alt')}
            />
            <div>
              <p className='text-sm font-medium !leading-tight text-neutral-900 md:text-base'>
                {t.rich('cashback_title', {
                  newline: () => <br />,
                })}
              </p>
            </div>
          </div>
        </div>
        <p className='mb-3 mt-4 font-sans text-xs font-bold'>
          {t('login_or_register')}
        </p>
        <div className='flex gap-3'>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
            onClick={handleThirdPartyLogin('google')}
            disabled={form.formState.isSubmitting}
          >
            <ImageCM
              src={'/September2023/mceclip1_21.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login google'
            />
          </Button>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
            onClick={handleThirdPartyLogin('facebook')}
            disabled={form.formState.isSubmitting}
          >
            <ImageCM
              src={'/September2023/mceclip0_86.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login facebook'
            />
          </Button>
          <Popover defaultOpen>
            <PopoverTrigger asChild>
              <Button
                variant={'outline'}
                size={'icon'}
                className={cn(
                  'rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50',
                  isQrMode ? 'border-2 hover:border-primary-500' : ''
                )}
                onClick={handleToggleQrMode}
                disabled={form.formState.isSubmitting}
              >
                <ImageCM
                  src={'/2026/01/22/mceclip0_53.png'}
                  width={26}
                  height={26}
                  className='h-full w-full object-scale-down'
                  alt='Login Zalo Mini App'
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side='top'
              className='z-[1000] w-fit max-w-[200px] border-none bg-neutral-900 p-2 text-center text-xs text-white'
            >
              {t('qr_instruction')}
              <PopoverArrow
                className='fill-neutral-900'
                width={12}
                height={12}
              />
            </PopoverContent>
          </Popover>
        </div>
        <Divider text={t('or')} />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <FormField
            control={form.control}
            name='username'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      autoFocus
                      floating
                      label={t('email_or_phone')}
                      {...field}
                      error={form.formState.errors?.username}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />

          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    floating
                    type={showPassword ? 'text' : 'password'}
                    {...field}
                    label={t('password')}
                    error={form.formState.errors?.password}
                    suffix={
                      <button
                        type='button'
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className='flex h-6 w-6 items-center justify-center focus:outline-none'
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <Button
              type='submit'
              className='w-full'
              variant='black'
              loading={form.formState.isSubmitting}
              ga-tracking-value={`login_account`}
              ga-tracking-label='login_account'
            >
              {t('login')}
            </Button>
            {error && <p className='mt-2 text-sm text-red-500'>{error}</p>}
          </div>
        </form>
      </Form>

      <div className='mt-4 flex justify-between'>
        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/register')
            } else {
              openRegister({ callback })
            }
          }}
        >
          {t('register_new')}
        </Button>

        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/forgot-password')
            } else {
              showPopup(<ForgotPasswordForm />)
            }
          }}
        >
          {t('forgot_password')}
        </Button>
      </div>
    </>
  )
}
